type ProviderLogo = string; // public URL path, e.g. '/icons/providers/sunlife.svg'

export type ProviderMeta = {
  canonicalKey: string; // ex: "SUN", "BMO", "RBC", "IA", "UV", etc.
  name: string; // ex: "Sun Life", "BMO Assurance", ...
  logo: ProviderLogo; // URL to static SVG
};

const providerAliasesToCanonical: Record<string, string> = {
  // Sun Life
  SUN: 'SUN',
  SLF: 'SUN',
  '25': 'SUN',

  // BMO
  BMO: 'BMO',
  '5': 'BMO',

  // RBC
  RBC: 'RBC',
  '24': 'RBC',

  // IA (iA Groupe financier)
  '20': 'IA',
  IA: 'IA',

  // Beneva
  '17': 'BENEVA',
  '27': 'BENEVA',
  '31': 'BENEVA',
  BENEVA: 'BENEVA',

  // UV
  '30': 'UV',
  UV: 'UV',

  // Assomption Vie
  '2': 'ASSOMPTION_VIE',
  ASSOMPTION_VIE: 'ASSOMPTION_VIE',

  // AIG / AXA / ASV
  '1': 'AIG',
  AIG: 'AIG',

  '3': 'AXA',
  AXA: 'AXA',

  '4': 'ASV',
  ASV: 'ASV',

  // Canada Life
  '6': 'CANADA_LIFE',
  CANADA_LIFE: 'CANADA_LIFE',

  // Blue Cross
  '8': 'BLUE_CROSS',
  BLUE_CROSS: 'BLUE_CROSS',

  // Desjardins
  '9': 'DESJARDINS',
  DESJARDINS: 'DESJARDINS',

  // Empire Vie
  '11': 'EMPIRE_VIE',
  EMPIRE_VIE: 'EMPIRE_VIE',

  // Equitable
  '12': 'EQUITABLE',
  EQUITABLE: 'EQUITABLE',

  // Foresters
  '13': 'FORESTERS',
  '21': 'FORESTERS',
  AFM: 'FORESTERS',
  FORESTERS: 'FORESTERS',

  // Great-West
  '14': 'GREAT_WEST',
  GREAT_WEST: 'GREAT_WEST',

  // Humania
  '15': 'HUMANIA',
  HUMANIA: 'HUMANIA',

  // Ivari
  '16': 'IVARI',
  IVARI: 'IVARI',

  // Standard Life
  '26': 'STANDARD_LIFE',
  STANDARD_LIFE: 'STANDARD_LIFE',

  // Banque Nationale du Canada
  '19': 'BANQUE_NATIONALE',
  '22': 'BANQUE_NATIONALE',
  NBC: 'BANQUE_NATIONALE',
  BANQUE_NATIONALE: 'BANQUE_NATIONALE',

  // Bridgehouse
  BIP: 'BIP',

  //Manuvie
  '23': 'MANUVIE',
  MMF: 'MANUVIE',

  // Autres
  CIG: 'CIG',
  TML: 'TML',
  FID: 'FID',

  // Cloutier Groupe Financier
  '999': 'CLOUTIER_GROUPE_FINANCIER',
  GC: 'CLOUTIER_GROUPE_FINANCIER',
};

const BASE = '/icons/providers';

const providerByCanonical: Record<string, Omit<ProviderMeta, 'canonicalKey'>> = {
  SUN: { name: 'Sun Life', logo: `${BASE}/sunlife.svg` },
  BMO: { name: 'BMO Assurance', logo: `${BASE}/bmo.svg` },
  RBC: { name: 'RBC Assurances', logo: `${BASE}/rbc.svg` },
  IA: { name: 'iA Groupe financier', logo: `${BASE}/ia.svg` },
  UV: { name: 'UV Assurance', logo: `${BASE}/uv.svg` },

  BENEVA: { name: 'Beneva', logo: `${BASE}/beneva.svg` },

  ASSOMPTION_VIE: { name: 'Assomption Vie', logo: `${BASE}/assomption-vie.svg` },
  AIG: { name: 'AIG', logo: `${BASE}/aig.svg` },
  AXA: { name: 'AXA', logo: `${BASE}/axa.svg` },
  ASV: { name: 'ASV', logo: `${BASE}/asv.svg` },

  CANADA_LIFE: { name: 'Canada Life', logo: `${BASE}/canada-vie.svg` },
  BLUE_CROSS: { name: 'Croix Bleue', logo: `${BASE}/croix-bleue.svg` },
  DESJARDINS: { name: 'Desjardins', logo: `${BASE}/desjardins.svg` },
  EMPIRE_VIE: { name: 'Empire Vie', logo: `${BASE}/empire-vie.svg` },
  EQUITABLE: { name: 'Equitable', logo: `${BASE}/equitable.svg` },
  FORESTERS: { name: 'Foresters', logo: `${BASE}/foresters.svg` },
  GREAT_WEST: { name: 'Great-West', logo: `${BASE}/great-west.svg` },
  HUMANIA: { name: 'Humania Assurance', logo: `${BASE}/humania.svg` },
  IVARI: { name: 'ivari', logo: `${BASE}/ivari.svg` },
  STANDARD_LIFE: { name: 'Standard Life', logo: `${BASE}/standard-life.svg` },

  MMF: { name: 'Manulife (MMF)', logo: `${BASE}/mmf.svg` },
  CIG: { name: 'Canada Insurance Group (CIG)', logo: `${BASE}/cig.svg` },
  TML: { name: 'The Manufacturers Life (TML)', logo: `${BASE}/tml.svg` },
  BIP: { name: 'Bridgehouse', logo: `${BASE}/bip.svg` },
  FID: { name: 'Fidelity', logo: `${BASE}/fidelity.svg` },
  BANQUE_NATIONALE: { name: 'Banque Nationale du Canada', logo: `${BASE}/nbc.svg` },

  MANUVIE: { name: 'Manuvie', logo: `${BASE}/mmf.svg` },

  CLOUTIER_GROUPE_FINANCIER: { name: 'Cloutier Groupe Financier', logo: `${BASE}/gc.svg` },
};

const UNKNOWN_LOGO = `${BASE}/unknown.svg`;

export function getProviderMeta(providerRaw?: string | null): ProviderMeta {
  const raw = (providerRaw ?? '').trim();
  const canonicalKey = providerAliasesToCanonical[raw] ?? raw;

  const meta = providerByCanonical[canonicalKey];
  if (!meta) {
    return { canonicalKey: canonicalKey || 'UNKNOWN', name: raw || 'Inconnu', logo: UNKNOWN_LOGO };
  }

  return { canonicalKey, ...meta };
}
